import { CSSProperties, useLayoutEffect, useRef } from "react";
import { MotionValue } from "framer-motion";
import SceneTrack, { useSceneRender } from "../SceneTrack";
import SceneCanvas from "../SceneCanvas";
import { box, clamp, easeIn, easeOut, lerp, range } from "../morph";
import { EXPERIENCES } from "../../../data/experience";
import { BASE, Beat, SCHEDULE, SPAN, firstLabel, labelFor, lastLabel, monthAt } from "./experienceSchedule";
import "./ExperienceScene.css";

/*
 * 03 · Experience — "Fold & Branch".
 *
 * A horizontal time axis, read like a version history. The playhead walks it
 * one month at a time. Reaching a role, a lane forks off the axis and that
 * role's card stands up on a hinge; its responsibilities appear as the
 * playhead crosses the role's tenure. When the role ends the lane merges back,
 * and the card folds down to a stub the width of its own time span.
 *
 * Responsibilities only — no commits, hashes or branch names. A reader can't
 * tell a decorative commit from a real one.
 */

const AX0 = 96;
const AX1 = 1104;
const AXIS_Y = 576;
const LANE_Y = 520;
const CARD_W = 640;
const HEAD_Y = 444;
const HEAD_H = 64;

const MW = (AX1 - AX0) / (SPAN + 1);
const xAt = (m: number) => AX0 + MW * (m + 0.5);

/** The shared schedule, placed on the horizontal axis. */
interface Job extends Beat {
    xs: number;
    xe: number;
    cardX: number;
    stubX: number;
    stubW: number;
}

const JOBS: Job[] = SCHEDULE.map((beat) => {
    const xs = xAt(beat.m0);
    const xe = xAt(beat.m1);
    return {
        ...beat,
        xs,
        xe,
        cardX: clamp((xs + xe) / 2 - CARD_W / 2, AX0, AX1 - CARD_W),
        stubX: xs - MW / 2,
        stubW: clamp(xe - xs + MW, 240, CARD_W),
    };
});

/** Tick boundaries: the start, every January, and the end. */
const TICKS = Array.from({ length: SPAN + 2 }, (_, i) => {
    const x = AX0 + i * MW;
    const major = i === 0 || i === SPAN + 1 || (BASE + i) % 12 === 0;
    return { x, major };
});

const yearMarks = TICKS.filter((t, i) => i > 0 && i <= SPAN && (BASE + i) % 12 === 0).map((t, _, arr) => ({
    x: t.x,
    year: Math.floor((BASE + Math.round((t.x - AX0) / MW)) / 12),
    n: arr.length,
}));

export default function ExperienceScene() {
    return <SceneTrack pin={260} tint="150, 220, 120">{(p) => <ExperienceCanvas p={p} />}</SceneTrack>;
}

function ExperienceCanvas({ p }: { p: MotionValue<number> }) {
    const root = useRef<HTMLDivElement>(null);
    const els = useRef<{
        prog: SVGPathElement;
        ticks: SVGLineElement[];
        ph: SVGLineElement;
        phDot: SVGCircleElement;
        digits: HTMLElement[];
        jobs: {
            fork: SVGPathElement;
            lane: SVGPathElement;
            merge: SVGPathElement;
            head: HTMLElement;
            body: HTMLElement;
            shades: HTMLElement[];
            items: HTMLElement[];
        }[];
    } | null>(null);

    useLayoutEffect(() => {
        const r = root.current;
        if (!r) return;
        const one = <T extends Element>(s: string) => r.querySelector(s) as T;
        const all = <T extends Element>(s: string, from: ParentNode = r) => Array.from(from.querySelectorAll(s)) as T[];
        els.current = {
            prog: one<SVGPathElement>(".ex-prog"),
            ticks: all<SVGLineElement>(".ex-tick"),
            ph: one<SVGLineElement>(".ex-ph"),
            phDot: one<SVGCircleElement>(".ex-ph-dot"),
            digits: all<HTMLElement>(".ex-odo-strip"),
            jobs: JOBS.map((_, j) => {
                const head = one<HTMLElement>(`[data-ex-head="${j}"]`);
                const body = one<HTMLElement>(`[data-ex-body="${j}"]`);
                return {
                    fork: one<SVGPathElement>(`[data-ex-fork="${j}"]`),
                    lane: one<SVGPathElement>(`[data-ex-lane="${j}"]`),
                    merge: one<SVGPathElement>(`[data-ex-merge="${j}"]`),
                    head,
                    body,
                    shades: [head, body].map((el) => el.querySelector(".ex-shade") as HTMLElement),
                    items: all<HTMLElement>("li", body),
                };
            }),
        };
    }, []);

    useSceneRender(p, (v) => {
        const E = els.current;
        if (!E) return;
        const m = monthAt(v);
        const phX = xAt(m);
        const lastMerge = JOBS[JOBS.length - 1].merge;
        const end = Math.max(phX, lerp(xAt(SPAN), AX1, range(v, lastMerge[0], lastMerge[1])));

        E.prog.style.strokeDashoffset = String(AX1 - AX0 - (end - AX0));
        E.ticks.forEach((t, i) => t.classList.toggle("is-on", TICKS[i].x <= end + 0.5));
        E.ph.setAttribute("x1", String(phX));
        E.ph.setAttribute("x2", String(phX));
        E.phDot.setAttribute("cx", String(phX));

        const { month, year } = labelFor(m);
        const digits = String(month).padStart(2, "0") + String(year);
        E.digits.forEach((d, i) => (d.style.transform = `translateY(${-Number(digits[i]) * 40}px)`));

        JOBS.forEach((job, j) => {
            const J = E.jobs[j];
            J.fork.style.strokeDashoffset = String(1 - range(v, job.fork[0], job.fork[1]));
            J.lane.style.strokeDashoffset = String(1 - clamp((phX - job.xs) / (job.xe - job.xs)));
            J.merge.style.strokeDashoffset = String(1 - range(v, job.merge[0], job.merge[1]));

            // The header stands up off the lane; the body unfolds above it.
            const ha = lerp(90, 0, easeOut(range(v, job.head[0], job.head[1])));
            const fold = job.fold ? easeIn(range(v, job.fold[0], job.fold[1])) : 0;
            const ba = lerp(90, 0, easeOut(range(v, job.body[0], job.body[1]))) + 90 * fold;
            const sw = job.stub ? easeOut(range(v, job.stub[0], job.stub[1])) : 0;

            box(J.head, lerp(job.cardX, job.stubX, sw), HEAD_Y, lerp(CARD_W, job.stubW, sw), HEAD_H);
            J.head.style.transform = `perspective(1200px) rotateX(${ha}deg)`;
            J.head.style.visibility = ha > 88 ? "hidden" : "visible";
            J.body.style.left = `${job.cardX}px`;
            J.body.style.transform = `perspective(1200px) rotateX(${ba}deg)`;
            J.body.style.visibility = ba > 88 ? "hidden" : "visible";
            J.shades[0].style.opacity = String((ha / 90) * 0.7);
            J.shades[1].style.opacity = String((Math.min(ba, 90) / 90) * 0.7);

            // Responsibilities appear as the playhead crosses the tenure.
            const n = J.items.length;
            J.items.forEach((li, i) => {
                const a = job.m0 + ((job.m1 - job.m0) * i) / n;
                const t = range(m, a, a + ((job.m1 - job.m0) * 0.7) / n);
                li.classList.toggle("is-on", t > 0);
                li.style.opacity = String(t);
                li.style.transform = `translateY(${6 * (1 - easeOut(t))}px)`;
            });
        });
    });

    return (
        <SceneCanvas tint="150, 220, 120" marker={{ index: 3, label: "Experience" }}>
            <div ref={root} className="ex">
                <h1 className="mx-abs scene-title" style={{ transform: "translate(96px,84px)" }}>
                    Work <span>Experience</span>
                </h1>
                <p className="mx-abs scene-sub" style={{ transform: "translate(96px,152px)", width: 560 }}>
                    Two roles so far, from backend APIs to owning the full stack.
                </p>

                <div className="mx-abs ex-odo" style={{ transform: "translate(884px,86px)" }} aria-hidden="true">
                    <span className="scene-label">Timeline</span>
                    <div className="ex-odo-digits mono">
                        {"00/0000".split("").map((ch, i) =>
                            ch === "/" ? (
                                <span key={i} className="ex-odo-sep">/</span>
                            ) : (
                                <span key={i} className="ex-odo-col">
                                    <span className="ex-odo-strip">
                                        {"0123456789".split("").map((d) => (
                                            <span key={d}>{d}</span>
                                        ))}
                                    </span>
                                </span>
                            )
                        )}
                    </div>
                </div>

                {/* A light running along the timeline while the page is at rest. */}
                <div className="ex-runner" style={{ left: AX0, top: AXIS_Y - 1, width: AX1 - AX0 }} aria-hidden="true">
                    <i />
                </div>
                <svg className="ex-svg" viewBox="0 0 1200 720" aria-hidden="true">
                    <path className="ex-axis" d={`M${AX0} ${AXIS_Y} H${AX1}`} />
                    {TICKS.map((t, i) => (
                        <line
                            key={i}
                            className="ex-tick"
                            x1={t.x}
                            x2={t.x}
                            y1={AXIS_Y + 4}
                            y2={AXIS_Y + (t.major ? 14 : 9)}
                        />
                    ))}
                    <path
                        className="ex-prog"
                        d={`M${AX0} ${AXIS_Y} H${AX1}`}
                        style={{ strokeDasharray: AX1 - AX0 }}
                    />
                    {JOBS.map((job, j) => (
                        <g key={j}>
                            <path
                                className="ex-branch"
                                data-ex-fork={j}
                                pathLength={1}
                                d={`M${job.xs - MW / 2} ${AXIS_Y} C${job.xs - MW / 4} ${AXIS_Y} ${job.xs - MW / 4} ${LANE_Y} ${job.xs} ${LANE_Y}`}
                            />
                            <path className="ex-branch" data-ex-lane={j} pathLength={1} d={`M${job.xs} ${LANE_Y} H${job.xe}`} />
                            <path
                                className="ex-branch"
                                data-ex-merge={j}
                                pathLength={1}
                                d={`M${job.xe} ${LANE_Y} C${job.xe + MW / 4} ${LANE_Y} ${job.xe + MW / 4} ${AXIS_Y} ${job.xe + MW / 2} ${AXIS_Y}`}
                            />
                        </g>
                    ))}
                    <line className="ex-ph" y1={498} y2={592} />
                    <circle className="ex-ph-dot" cy={AXIS_Y} r={4.5} />
                </svg>

                <span className="mx-abs ex-tl" style={{ transform: `translate(${AX0}px,598px)` }}>
                    {firstLabel}
                </span>
                {yearMarks.map((y) => (
                    <span key={y.x} className="mx-abs ex-tl" style={{ transform: `translate(${y.x}px,598px)` }}>
                        {y.year}
                    </span>
                ))}
                <span className="mx-abs ex-tl" style={{ transform: `translate(${AX1}px,598px) translateX(-100%)` }}>
                    {lastLabel}
                </span>

                {EXPERIENCES.map((exp, j) => (
                    <div key={exp.company} className="ex-job" style={{ "--i": j } as CSSProperties}>
                        <div className="ex-body" data-ex-body={j} style={{ width: CARD_W, bottom: 720 - HEAD_Y }}>
                            <ul>
                                {exp.responsibilities.map((r) => (
                                    <li key={r}>{r}</li>
                                ))}
                            </ul>
                            <i className="ex-shade" />
                        </div>
                        <div className="ex-head" data-ex-head={j}>
                            <div className="ex-head-row">
                                <h3>{exp.title}</h3>
                                <span className="ex-period mono">{exp.period}</span>
                            </div>
                            <div className="ex-head-row ex-head-sub">
                                <span>{exp.company}</span>
                                <span className="ex-focus mono">{exp.focus}</span>
                            </div>
                            <i className="ex-shade" />
                        </div>
                    </div>
                ))}
            </div>
        </SceneCanvas>
    );
}
